import React, { memo } from 'react';
import { Tooltip } from '@mui/material';
import type { DoctorShift } from '../../../../../types/doctorShift';
import { extractTimeHm } from '../../../../../utils/shiftTime';
import { ShiftBarNormal, ShiftBarUrgent } from '../../MonthlyDoctorsShift.styles';
import type { TimelinePosition } from '../../hooks/useTimelinePosition';

/**
 * ShiftBar Props
 */
interface ShiftBarProps {
  /** シフトデータ（結合バーの場合は代表=先頭行） */
  shift: DoctorShift;
  /** 位置情報 */
  position: TimelinePosition;
  /** 表示用開始時刻（HH:mm）。未指定時は shift から抽出 */
  startTime?: string;
  /** 表示用終了時刻（HH:mm）。未指定時は shift から抽出 */
  endTime?: string;
  /** 結合された行数（連続シフトを1本にまとめた場合に2以上） */
  shiftCount?: number;
  /** クリックハンドラー */
  onClick: (shift: DoctorShift) => void;
  /** 読み取り専用モード */
  readOnly?: boolean;
}

/**
 * シフトバーコンポーネント
 *
 * @description
 * 医師シフトをカラーバーで表示する。
 * クリックで編集ポップアップを開く。ツールチップで詳細情報を表示。
 *
 * @param {ShiftBarProps} props - コンポーネントProps
 * @returns {React.ReactElement} シフトバー
 *
 * @example
 * <ShiftBar
 *   shift={shift}
 *   position={{ left: 10, width: 20 }}
 *   onClick={handleShiftClick}
 * />
 */
const ShiftBar: React.FC<ShiftBarProps> = memo(({
  shift,
  position,
  startTime,
  endTime,
  shiftCount = 1,
  onClick,
  readOnly = false,
}) => {
  const isUrgent = shift.slot_type === 'urgent';
  const displayStart = startTime ?? extractTimeHm(shift.start_time);
  const displayEnd = endTime ?? extractTimeHm(shift.end_time);
  const BarComponent = isUrgent ? ShiftBarUrgent : ShiftBarNormal;

  /**
   * クリックハンドラー
   */
  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!readOnly) {
      onClick(shift);
    }
  };

  /**
   * ツールチップの内容
   */
  const tooltipContent = (
    <>
      <div>{shift.doctor_name || shift.doctor_full_name || ''}</div>
      <div>
        {displayStart} - {displayEnd}
        {shiftCount > 1 && `（${shiftCount}枠）`}
      </div>
      {isUrgent && <div style={{ color: '#ff6b6b' }}>急募枠</div>}
      {readOnly && <div style={{ color: '#999' }}>（閲覧のみ）</div>}
    </>
  );

  return (
    <Tooltip title={tooltipContent} arrow placement="top">
      <BarComponent
        onClick={handleClick}
        style={{
          left: `${position.left}%`,
          width: `${position.width}%`,
          cursor: readOnly ? 'default' : 'pointer',
        }}
      >
        {shift.doctor_name || shift.doctor_full_name || ''}先生 {displayStart}~{displayEnd}
      </BarComponent>
    </Tooltip>
  );
});

ShiftBar.displayName = 'ShiftBar';

export { ShiftBar };
